import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Network, Wifi, Plus, Search, ChevronDown, ChevronUp, Shield, CheckCircle2, AlertTriangle } from "lucide-react";

const fmt = (v) =>
  typeof v === "number"
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "USD" }).format(v)
    : v;

// PoE+ port count per FortiSwitch model — values cross-checked against the
// Fortinet FortiSwitch datasheets. "FPOE" suffix = every port is PoE+/++,
// "POE" alone = roughly half are PoE+. Used by the physical-coherence check
// at the bottom of the panel.
const SWITCH_POE_PORTS = {
  // 8-port desktop
  "FS-108E-POE": 8, "FS-108F-POE": 8, "FS-108F-FPOE": 8,
  // 24-port access
  "FS-124F-POE": 12, "FS-124F-FPOE": 24, "FS-124E-POE": 12, "FS-124E-FPOE": 24,
  "FS-224E-POE": 12,
  // 48-port access
  "FS-148F-POE": 24, "FS-148F-FPOE": 48,
  "FS-148E-POE": 24, "FS-148E-FPOE": 48,
  "FS-248E-POE": 24, "FS-248E-FPOE": 48,
  // 24/48-port aggregation w/ PoE
  "FS-424E-POE": 24, "FS-424E-FPOE": 24,
  "FS-448E-POE": 24, "FS-448E-FPOE": 48,
};

// Fallback when a partner picks a model we haven't catalogued yet — infer the
// PoE port count from the SKU naming convention.
function inferPoEPorts(sku) {
  if (!sku) return 0;
  const upper = sku.toUpperCase();
  if (SWITCH_POE_PORTS[upper] !== undefined) return SWITCH_POE_PORTS[upper];
  // FS-{model}{F|E}-{POE|FPOE}. Extract the digit count from the model id.
  const m = upper.match(/^FS-?(\d{2,3})/);
  if (!m) return 0;
  const total = parseInt(m[1].slice(-2), 10) || 0; // last 2 digits = port count
  if (/FPOE/.test(upper)) return total;
  if (/POE/.test(upper))  return Math.floor(total / 2);
  return 0; // non-PoE model
}

// Recommended defaults — picked from the official price list, matching the
// vast majority of WAF-region partner BOMs we've audited.
const DEFAULT_SWITCH_SKU = "FS-148F-FPOE";   // 48 ports full PoE+ — covers ~40 users / unit
const DEFAULT_AP_BASE = "FAP-231K";          // mid-range Wi-Fi 6, ~25 users / AP
const DEFAULT_AP_REGION = "E";               // Europe regulatory variant

// Heuristics — kept intentionally simple so partners can override without a
// PhD in network design.
function suggestSwitchQty(users, sites) {
  // 1 switch covers ~40 PoE+ ports. Round up.
  const byUsers = Math.max(1, Math.ceil((Number(users) || 0) / 40));
  // But at least 1 switch per site if multiple sites are involved.
  return Math.max(byUsers, Number(sites) || 1);
}

function suggestApQty(users, sites) {
  // ~25 active users per AP (industry rule of thumb for Wi-Fi 6 mid-range).
  const byUsers = Math.max(1, Math.ceil((Number(users) || 0) / 25));
  // Ensure decent coverage: at least 2 APs per site.
  return Math.max(byUsers, 2 * (Number(sites) || 1));
}

/**
 * NetworkingAccessPanel — surfaces the LAN access layer (FortiSwitch +
 * FortiAP) as two prominent cards above the generic AddonsPanel in the
 * « BOM Networking » mode of the configurator.
 *
 * Each card auto-suggests a representative SKU and a quantity based on user
 * count + sites, lets the partner override either, and pushes the line into
 * the shared `addons` state so the engine picks it up at /api/configure time.
 */
export default function NetworkingAccessPanel({ value, onChange, users, sites }) {
  const [fapRegions, setFapRegions] = useState([]);
  const [apRegion, setApRegion] = useState(DEFAULT_AP_REGION);
  const [switchSku, setSwitchSku] = useState(DEFAULT_SWITCH_SKU);
  const [apBaseSku, setApBaseSku] = useState(DEFAULT_AP_BASE);
  // Collapsed by default like other secondary panels, but pulses red until
  // the partner expands it for the first time so the section stays visible.
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [switchQty, setSwitchQty] = useState(suggestSwitchQty(users, sites));
  const [apQty, setApQty] = useState(suggestApQty(users, sites));
  // Live prices fetched from the official PL.
  const [prices, setPrices] = useState({}); // { sku: number }
  // Picker state — null = closed, "switch" | "ap" = open for that family.
  const [picker, setPicker] = useState(null);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  const apSku = useMemo(() => `${apBaseSku}-${apRegion}`, [apBaseSku, apRegion]);

  // Re-suggest quantities when users / sites change AND the partner hasn't
  // manually overridden them in this session (we always overwrite — the form
  // values are the source of truth here, partners can override after).
  useEffect(() => {
    setSwitchQty(suggestSwitchQty(users, sites));
    setApQty(suggestApQty(users, sites));
  }, [users, sites]);

  // Load FortiAP regulatory regions (same endpoint AddonsPanel uses).
  useEffect(() => {
    api.get("/solutions/fortiap/regions")
      .then((r) => setFapRegions(r.data?.items || []))
      .catch(() => setFapRegions([]));
  }, []);

  // Live-price the currently selected SKUs.
  useEffect(() => {
    const skus = [switchSku, apSku].filter(Boolean);
    skus.forEach((s) => {
      if (prices[s] !== undefined) return;
      api.get("/pricelist/base/search", { params: { q: s, limit: 5 } })
        .then((r) => {
          const exact = (r.data?.items || []).find((it) => it.sku === s);
          if (exact) setPrices((p) => ({ ...p, [s]: exact.price || 0 }));
          else setPrices((p) => ({ ...p, [s]: 0 }));
        })
        .catch(() => setPrices((p) => ({ ...p, [s]: 0 })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchSku, apSku]);

  // SKU picker — fetches against /solutions/{id}/skus
  useEffect(() => {
    if (!picker) return;
    setPickerLoading(true);
    const solId = picker === "switch" ? "fortiswitch" : "fortiap";
    api.get(`/solutions/${solId}/skus`, { params: { q: pickerQuery, limit: 30 } })
      .then((r) => setPickerItems(r.data?.items || []))
      .catch(() => setPickerItems([]))
      .finally(() => setPickerLoading(false));
  }, [picker, pickerQuery]);

  const openPicker = (which) => {
    setPicker(which);
    setPickerQuery("");
    setPickerItems([]);
  };
  const closePicker = () => {
    setPicker(null);
    setPickerQuery("");
    setPickerItems([]);
  };
  const pickSku = (item) => {
    if (picker === "switch") {
      setSwitchSku(item.sku);
    } else if (picker === "ap") {
      // Strip the trailing -X regulatory suffix so the region selector keeps
      // working.
      const base = /^FAP-.+-[A-Z]$/.test(item.sku)
        ? item.sku.replace(/-[A-Z]$/, "")
        : item.sku;
      setApBaseSku(base);
    }
    closePicker();
  };

  const addLine = (solution_id, sku, name, price, qty) => {
    if (!sku || qty <= 0) return;
    const existing = value.find((v) => v.sku === sku);
    if (existing) {
      onChange(
        value.map((v) =>
          v.sku === sku ? { ...v, quantity: Number(v.quantity) + Number(qty) } : v
        )
      );
      toast.success(`${name} — quantité +${qty}`);
    } else {
      onChange([...value, { solution_id, sku, name, price, quantity: qty }]);
      toast.success(`${name} ×${qty} ajouté au BOM`);
    }
  };

  const switchPrice = prices[switchSku] || 0;
  const apPrice = prices[apSku] || 0;
  const switchTotal = switchPrice * switchQty;
  const apTotal = apPrice * apQty;

  // Physical coherence check — make sure the selected switches can host every
  // FortiAP via PoE+. APs need 1 PoE+ port each.
  const portsPerSwitch = useMemo(() => inferPoEPorts(switchSku), [switchSku]);
  const availablePorts = portsPerSwitch * switchQty;
  const coherenceOk = portsPerSwitch === 0 || apQty <= availablePorts;
  // How many additional switches would solve the shortfall.
  const switchesShortBy = useMemo(() => {
    if (coherenceOk || portsPerSwitch === 0) return 0;
    return Math.ceil((apQty - availablePorts) / portsPerSwitch);
  }, [coherenceOk, portsPerSwitch, apQty, availablePorts]);

  // Friendly region label for the FortiAP name.
  const regionLabel = useMemo(
    () => fapRegions.find((r) => r.code === apRegion)?.label || apRegion,
    [fapRegions, apRegion]
  );

  return (
    <div
      className={`mt-6 border bg-white rounded-sm overflow-hidden transition-colors ${
        hasOpened ? "border-zinc-300" : "border-zinc-300 pythagore-attention-pulse"
      }`}
      data-testid="networking-access-panel"
    >
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          setHasOpened(true);
        }}
        className="w-full text-left px-5 py-4 border-b border-zinc-200 bg-gradient-to-r from-[#D42B1E]/[0.05] to-transparent hover:from-[#D42B1E]/[0.08] transition-colors"
        data-testid="networking-access-panel-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <Network className="h-5 w-5 text-[#D42B1E] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-zinc-900 flex items-center flex-wrap gap-2">
              Couche d'accès LAN &amp; Wi-Fi
              <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#D42B1E] text-white text-[10px] font-mono uppercase tracking-wide">
                FortiSwitch · FortiAP
              </span>
              {!hasOpened && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-mono uppercase tracking-wide">
                  À configurer
                </span>
              )}
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">
              Dimensionnement automatique pour {Number(users) || 0} utilisateurs sur {Number(sites) || 1} site{(Number(sites) || 1) > 1 ? "s" : ""} — ajustez SKU + quantité, puis ajoutez à la BOM.
            </p>
          </div>
          {open ? (
            <ChevronUp className="h-5 w-5 text-zinc-500 shrink-0" />
          ) : (
            <ChevronDown className="h-5 w-5 text-zinc-500 shrink-0" />
          )}
        </div>
      </button>

      {open && (
        <>
          {/* FortiLink embedded controller notice — every FortiGate ships with
              a built-in FortiSwitch/FortiAP controller, so no separate SKU is
              required for management. */}
          <div className="px-5 py-2.5 border-b border-zinc-200 bg-emerald-50/50 flex items-start gap-2.5" data-testid="networking-fortilink-note">
            <Shield className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
            <p className="text-[11.5px] text-emerald-900 leading-snug">
              <span className="font-semibold">Contrôleur embarqué</span> — Le FortiGate sélectionné dans la BOM gère nativement les FortiSwitch (via FortiLink) et les FortiAP (via CAPWAP/FortiLink) : <span className="font-semibold">aucune licence ni appliance de management additionnelle</span> n'est requise pour piloter cette couche d'accès.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
        {/* ───────── FortiSwitch card ───────── */}
        <div className="p-5" data-testid="networking-switch-card">
          <div className="flex items-center gap-2 mb-3">
            <Network className="h-4 w-4 text-zinc-700" />
            <h3 className="text-sm font-semibold text-zinc-900">Switchs LAN — FortiSwitch</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">SKU sélectionné</Label>
              <div className="mt-1 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => openPicker("switch")}
                  className="flex-1 inline-flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-zinc-300 bg-white hover:border-[#D42B1E]/50 hover:bg-zinc-50 transition-colors text-left"
                  data-testid="networking-switch-picker-toggle"
                >
                  <span className="font-mono text-xs text-zinc-900 truncate">{switchSku}</span>
                  <Search className="h-3 w-3 text-zinc-400 shrink-0" />
                </button>
                <span className="text-xs text-zinc-700 font-mono tabular-nums shrink-0">
                  {fmt(switchPrice)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="switch-qty" className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                  Quantité
                </Label>
                <Input
                  id="switch-qty"
                  type="number"
                  min={1}
                  value={switchQty}
                  onChange={(e) => setSwitchQty(Math.max(1, Number(e.target.value) || 1))}
                  className="rounded-sm font-mono mt-1"
                  data-testid="networking-switch-qty"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Total</Label>
                <div className="mt-1 px-3 py-2 rounded-sm bg-zinc-50 border border-zinc-200 text-zinc-900 font-mono text-sm tabular-nums">
                  {fmt(switchTotal)}
                </div>
              </div>
            </div>

            <p className="text-[11px] text-zinc-500">
              Suggestion : ~40 ports PoE+ par switch · {suggestSwitchQty(users, sites)} unité{suggestSwitchQty(users, sites) > 1 ? "s" : ""} recommandée{suggestSwitchQty(users, sites) > 1 ? "s" : ""}.
            </p>

            <Button
              type="button"
              onClick={() =>
                addLine("fortiswitch", switchSku, `FortiSwitch ${switchSku}`, switchPrice, switchQty)
              }
              disabled={!switchSku || switchQty <= 0}
              className="w-full bg-[#D42B1E] hover:bg-[#B32218] rounded-sm"
              data-testid="networking-switch-add"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Ajouter au BOM
            </Button>
          </div>
        </div>

        {/* ───────── FortiAP card ───────── */}
        <div className="p-5" data-testid="networking-ap-card">
          <div className="flex items-center gap-2 mb-3">
            <Wifi className="h-4 w-4 text-zinc-700" />
            <h3 className="text-sm font-semibold text-zinc-900">Bornes Wi-Fi — FortiAP</h3>
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">SKU sélectionné</Label>
                <button
                  type="button"
                  onClick={() => openPicker("ap")}
                  className="mt-1 w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-zinc-300 bg-white hover:border-[#D42B1E]/50 hover:bg-zinc-50 transition-colors text-left"
                  data-testid="networking-ap-picker-toggle"
                >
                  <span className="font-mono text-xs text-zinc-900 truncate">{apBaseSku}</span>
                  <Search className="h-3 w-3 text-zinc-400 shrink-0" />
                </button>
              </div>
              <div>
                <Label htmlFor="ap-region" className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                  Région réglementaire
                </Label>
                <select
                  id="ap-region"
                  value={apRegion}
                  onChange={(e) => setApRegion(e.target.value)}
                  className="mt-1 w-full h-9 px-2 rounded-sm border border-zinc-300 bg-white text-sm focus:outline-none focus:border-[#D42B1E]"
                  data-testid="networking-ap-region"
                >
                  {fapRegions.length === 0 && <option value="E">Europe (E)</option>}
                  {Object.entries(
                    fapRegions.reduce((acc, r) => {
                      const k = r.region || "Autre";
                      (acc[k] ||= []).push(r);
                      return acc;
                    }, {})
                  )
                    // Stable, sensible zone order: EMEA first (default Europe),
                    // then APAC, then Americas, then mixed buckets.
                    .sort(([a], [b]) => {
                      const order = { "EMEA": 0, "APAC": 1, "Americas": 2, "Americas/APAC": 3, "Générique": 9 };
                      return (order[a] ?? 5) - (order[b] ?? 5);
                    })
                    .map(([zone, rows]) => (
                      <optgroup key={zone} label={zone}>
                        {rows.map((r) => (
                          <option key={r.code} value={r.code}>
                            {r.label} ({r.code})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ap-qty" className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                  Quantité
                </Label>
                <Input
                  id="ap-qty"
                  type="number"
                  min={1}
                  value={apQty}
                  onChange={(e) => setApQty(Math.max(1, Number(e.target.value) || 1))}
                  className="rounded-sm font-mono mt-1"
                  data-testid="networking-ap-qty"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Total</Label>
                <div className="mt-1 px-3 py-2 rounded-sm bg-zinc-50 border border-zinc-200 text-zinc-900 font-mono text-sm tabular-nums">
                  {fmt(apTotal)}
                </div>
              </div>
            </div>

            <p className="text-[11px] text-zinc-500">
              Final SKU : <span className="font-mono">{apSku}</span> · ~25 utilisateurs / AP, min. 2 par site · {suggestApQty(users, sites)} unité{suggestApQty(users, sites) > 1 ? "s" : ""} recommandée{suggestApQty(users, sites) > 1 ? "s" : ""}.
            </p>

            <Button
              type="button"
              onClick={() =>
                addLine("fortiap", apSku, `FortiAP ${apBaseSku} — ${regionLabel}`, apPrice, apQty)
              }
              disabled={!apSku || apQty <= 0}
              className="w-full bg-[#D42B1E] hover:bg-[#B32218] rounded-sm"
              data-testid="networking-ap-add"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Ajouter au BOM
            </Button>
          </div>
        </div>
      </div>

      {/* Physical coherence check — verifies every FortiAP gets a PoE+ port. */}
      <div
        className={`border-t border-zinc-200 px-5 py-3 flex items-center gap-3 ${
          coherenceOk ? "bg-emerald-50/50" : "bg-rose-50/60"
        }`}
        data-testid="networking-coherence"
        data-coherence={coherenceOk ? "ok" : "ko"}
      >
        {coherenceOk ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-rose-700 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className={`text-[12px] font-semibold ${coherenceOk ? "text-emerald-900" : "text-rose-900"}`}>
            Cohérence physique :
            {coherenceOk ? (
              <> {portsPerSwitch === 0 ? "modèle de switch non-PoE — vérifier manuellement la couverture" : `${apQty} APs ≤ ${availablePorts} ports PoE+ disponibles (${switchQty}×${switchSku} = ${portsPerSwitch} ports/unité) — OK`}</>
            ) : (
              <> {apQty} APs &gt; {availablePorts} ports PoE+ disponibles ({switchQty}×{switchSku} = {portsPerSwitch} ports/unité) — capacité insuffisante</>
            )}
          </p>
          {!coherenceOk && (
            <p className="text-[11px] text-rose-800 mt-0.5">
              Augmentez le nombre de switchs de <strong>+{switchesShortBy}</strong> unité{switchesShortBy > 1 ? "s" : ""} pour héberger les {apQty} bornes Wi-Fi sur PoE+.
            </p>
          )}
        </div>
        {!coherenceOk && (
          <Button
            type="button"
            size="sm"
            onClick={() => setSwitchQty((q) => q + switchesShortBy)}
            className="bg-rose-600 hover:bg-rose-700 rounded-sm shrink-0"
            data-testid="networking-coherence-fix"
            title={`Ajouter ${switchesShortBy} switch(s) supplémentaire(s)`}
          >
            +{switchesShortBy} switch{switchesShortBy > 1 ? "s" : ""}
          </Button>
        )}
      </div>

      {/* SKU picker — inline panel below the cards */}
      {picker && (
        <div
          className="border-t border-zinc-200 bg-zinc-50/40 p-4"
          data-testid={`networking-picker-${picker}`}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600">
              {picker === "switch" ? "Choisir un modèle FortiSwitch" : "Choisir un modèle FortiAP"}
            </p>
            <button
              type="button"
              onClick={closePicker}
              className="text-[11px] text-zinc-500 hover:text-zinc-900 underline underline-offset-2"
              data-testid="networking-picker-close"
            >
              Fermer
            </button>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
            <Input
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              placeholder={picker === "switch" ? "ex. 148F-FPOE, 124F, 24 ports…" : "ex. 231K, 432, Wi-Fi 6E…"}
              className="pl-9 rounded-sm"
              data-testid="networking-picker-search"
              autoFocus
            />
          </div>
          <div className="max-h-72 overflow-y-auto rounded-sm border border-zinc-200 bg-white">
            {pickerLoading && (
              <div className="px-3 py-4 text-center text-xs text-zinc-500">Chargement…</div>
            )}
            {!pickerLoading && pickerItems.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-zinc-500">Aucun résultat.</div>
            )}
            {!pickerLoading && pickerItems.map((it) => {
              // For FortiAP, show only the base SKU (strip region suffix) to
              // avoid 8 duplicate rows for the same hardware.
              const displaySku = picker === "ap" && /^FAP-.+-[A-Z]$/.test(it.sku)
                ? it.sku.replace(/-[A-Z]$/, "")
                : it.sku;
              return (
                <button
                  key={it.sku}
                  type="button"
                  onClick={() => pickSku(it)}
                  className="w-full px-3 py-2 hover:bg-zinc-50 text-left flex items-center justify-between gap-3 border-b border-zinc-100 last:border-0"
                  data-testid={`networking-picker-pick-${it.sku}`}
                >
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-zinc-900">{displaySku}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{it.name}</p>
                  </div>
                  <span className="text-xs font-mono text-zinc-700 shrink-0">{fmt(it.price)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
